import React, { useState, useMemo, useEffect } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, redDark, amber, amberLight, amberDark, useTheme } from '../tokens';
import { getDeadlineStatus } from '../helpers';
import BankRecPanel from './BankRecPanel';
import BankRecJournalEntries from './BankRecJournalEntries';
import PanelWorkpaperView from './PanelWorkpaperView';

const teal = '#0D9488';
// const tealLight = '#CCFBF1';
const purple = '#7C3AED';
const purpleLight = '#EDE9FE';
const purpleDark = '#5B21B6';

// Block reasons available for future use in task blocking UI
// const BLOCK_REASONS = [
//   'Waiting for external documents',
//   'Pending prior task completion',
//   'System access issue',
//   'Waiting for management approval',
//   'Quarterly filing -- not due yet',
//   'Vendor / third-party delay',
//   'Under investigation',
//   'Other',
// ];

// Panel type labels for display
const PANEL_TYPE_LABELS = {
  bank_reconciliation: 'Bank Reconciliation',
  investment_schedule: 'Investment Schedule',
  aging_schedule: 'Aging Schedule',
  rollforward: 'Rollforward',
  depreciation_schedule: 'Depreciation Schedule',
  amortization_schedule: 'Amortization Schedule',
  payroll_reconciliation: 'Payroll Reconciliation',
  tax_reconciliation: 'Tax Reconciliation',
  debt_schedule: 'Debt Schedule',
  lease_schedule: 'Lease Schedule',
  fx_revaluation: 'FX Revaluation',
  intercompany: 'Intercompany',
  journal_entry: 'Journal Entry',
  variance_analysis: 'Variance Analysis',
  checklist: 'Checklist',
  equity_rollforward: 'Equity Rollforward',
  cash_flow_indirect: 'Cash Flow (Indirect)',
  consolidation: 'Consolidation',
  generic: 'General',
};

function TaskRow({ task, onToggleComplete, onToggleReview, onToggleBlocked, onUpdateDeadline, onOpenTask, onRemoveTask, mockToday, userInitials, t }) {
  const [hovered, setHovered] = useState(false);
  const dlStatus = task.blocked ? 'blocked' : getDeadlineStatus(task.dueDate, task.done, mockToday);
  const isOverdue = dlStatus === 'overdue';
  const isDueSoon = dlStatus === 'due-soon';
  const canComplete = !task.blocked;
  const canReview = task.done && !task.reviewed && task.completedBy !== userInitials;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr 70px 24px 24px 18px',
        alignItems: 'center',
        padding: '8px 0',
        gap: 8,
        borderBottom: `1px solid ${t.borderLight || t.border}`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Prep checkbox */}
      <div
        onClick={e => { e.stopPropagation(); if (canComplete) onToggleComplete(task.id); }}
        style={{ cursor: canComplete ? 'pointer' : 'not-allowed' }}
      >
        {task.done ? (
          <div style={{
            width: 18, height: 18, borderRadius: 4, background: teal,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>{'\\u2713'}</span>
          </div>
        ) : (
          <div style={{
            width: 18, height: 18, borderRadius: 4,
            border: `1.5px solid ${task.blocked ? purple + '60' : t.border}`,
          }} />
        )}
      </div>

      {/* Task name + meta */}
      <div
        onClick={() => onOpenTask && onOpenTask(task.id)}
        style={{ cursor: 'pointer', minWidth: 0 }}
      >
        <div style={{
          fontSize: 12, fontFamily: sans, fontWeight: 500,
          color: task.reviewed ? t.textMuted : t.textPrimary,
          textDecoration: task.reviewed ? 'line-through' : 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {task.name}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
          {task.completedBy && (
            <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint }}>
              P: {task.completedBy}
            </span>
          )}
          {task.reviewedBy && (
            <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint }}>
              R: {task.reviewedBy}
            </span>
          )}
          {task.blocked && (
            <span style={{ fontSize: 8, padding: '0px 5px', borderRadius: 6, background: purpleLight, color: purpleDark, fontWeight: 600 }}>
              Blocked
            </span>
          )}
          {isOverdue && !task.blocked && (
            <span style={{ fontSize: 8, padding: '0px 5px', borderRadius: 6, background: redLight, color: redDark, fontWeight: 600 }}>
              Overdue
            </span>
          )}
          {isDueSoon && !task.blocked && (
            <span style={{ fontSize: 8, padding: '0px 5px', borderRadius: 6, background: amberLight, color: amberDark, fontWeight: 600 }}>
              Due soon
            </span>
          )}
        </div>
      </div>

      {/* Due date */}
      <span style={{
        fontSize: 10, fontFamily: mono, textAlign: 'right',
        color: isOverdue ? red : isDueSoon ? amber : t.textFaint,
        fontWeight: isOverdue ? 600 : 400,
      }}>
        {task.dueDate ? new Date(task.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}
      </span>

      {/* Assignee initial */}
      <div style={{
        width: 22, height: 22, borderRadius: '50%',
        background: task.assignedTo ? teal + '20' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 8, fontWeight: 700, fontFamily: mono, color: teal,
      }}>
        {task.assignedTo || ''}
      </div>

      {/* Review checkbox */}
      <div
        onClick={e => { e.stopPropagation(); if (canReview) onToggleReview(task.id); }}
        style={{ cursor: canReview ? 'pointer' : 'default' }}
      >
        {task.reviewed ? (
          <div style={{
            width: 18, height: 18, borderRadius: 4, background: green,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>{'\\u2713'}</span>
          </div>
        ) : task.done ? (
          <div style={{
            width: 18, height: 18, borderRadius: 4,
            border: `1.5px solid ${canReview ? teal : t.border}`,
            opacity: canReview ? 1 : 0.4,
          }} />
        ) : (
          <span style={{ fontSize: 10, color: t.textFaint }}>--</span>
        )}
      </div>

      {/* Remove button (visible on hover) */}
      {onRemoveTask && hovered ? (
        <button
          onClick={e => { e.stopPropagation(); onRemoveTask(task.id); }}
          title="Remove task"
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer',
            fontSize: 12, color: t.textFaint, padding: 0, lineHeight: 1,
            opacity: 0.6,
          }}
        >&times;</button>
      ) : <span />}
    </div>
  );
}

export default function PanelDetailView({
  panel,
  tasks,
  onToggleComplete,
  onToggleReview,
  onToggleBlocked,
  onUpdateDeadline,
  onOpenTask,
  onAddTask,
  onRemoveTask,
  mockToday,
  userInitials,
  glMappings,
  companyId,
  selectedMonth,
}) {
  const t = useTheme();
  const WORKPAPER_TYPES_INIT = ['rollforward', 'depreciation_schedule', 'amortization_schedule', 'aging_schedule', 'debt_schedule', 'payroll_reconciliation', 'tax_reconciliation', 'lease_schedule', 'equity_rollforward', 'variance_analysis', 'investment_schedule'];
  const defaultTab = panel.panelType === 'bank_reconciliation' ? 'reconciliation' : WORKPAPER_TYPES_INIT.includes(panel.panelType) ? 'workpaper' : 'tasks';
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Reset tab when panel changes
  useEffect(() => {
    const newDefault = panel.panelType === 'bank_reconciliation' ? 'reconciliation' : WORKPAPER_TYPES_INIT.includes(panel.panelType) ? 'workpaper' : 'tasks';
    setActiveTab(newDefault);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel.id]);
  const [addingTask, setAddingTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');

  const stats = useMemo(() => {
    const total = tasks.length;
    const done = tasks.filter(tk => tk.done).length;
    const reviewed = tasks.filter(tk => tk.reviewed).length;
    const blocked = tasks.filter(tk => tk.blocked).length;
    const overdue = tasks.filter(tk => !tk.blocked && !tk.done && getDeadlineStatus(tk.dueDate, tk.done, mockToday) === 'overdue').length;
    return { total, done, reviewed, blocked, overdue };
  }, [tasks, mockToday]);

  // Group tasks by legacy sub-section (for panels that aggregate multiple old sections)
  const subSections = useMemo(() => {
    const sections = [];
    const seen = new Set();
    tasks.forEach(task => {
      if (!seen.has(task.section)) {
        seen.add(task.section);
        sections.push(task.section);
      }
    });
    return sections;
  }, [tasks]);

  // GL accounts from imported TB mapped to this panel
  const panelAccounts = useMemo(() => {
    if (!glMappings || !glMappings.accounts || !panel) return [];
    return glMappings.accounts.filter(a => a.panelId === panel.id);
  }, [glMappings, panel]);

  const panelBalance = useMemo(() => {
    return panelAccounts.reduce((sum, a) => sum + (a.balance || 0), 0);
  }, [panelAccounts]);

  const isBankRec = panel.panelType === 'bank_reconciliation';
  const WORKPAPER_TYPES = ['rollforward', 'depreciation_schedule', 'amortization_schedule', 'aging_schedule', 'debt_schedule', 'payroll_reconciliation', 'tax_reconciliation', 'lease_schedule', 'equity_rollforward', 'variance_analysis', 'investment_schedule'];
  const hasWorkpaper = !isBankRec && WORKPAPER_TYPES.includes(panel.panelType);

  const TABS = [
    ...(isBankRec ? [
      { id: 'reconciliation', label: 'Reconciliation' },
      { id: 'journal-entries', label: 'Journal Entries' },
    ] : []),
    ...(hasWorkpaper ? [{ id: 'workpaper', label: 'Workpaper' }] : []),
    { id: 'tasks', label: 'Tasks' },
    { id: 'data', label: 'Data' },
    { id: 'documents', label: 'Documents' },
    { id: 'activity', label: 'Activity' },
  ];

  if (!panel) return null;

  // PP&E uses its own dedicated view with 6 sub-sections — hide the parent tab bar.
  const isPPE = panel.id === 'bs-nca-ppe';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Panel header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}` }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: isPPE ? 0 : 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>
              {panel.label}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 3 }}>
              {panel.glRange && (
                <span style={{ fontSize: 10, fontFamily: mono, color: t.textFaint }}>
                  GL {panel.glRange}
                </span>
              )}
              <span style={{ fontSize: 10, fontFamily: sans, color: t.textFaint }}>
                {PANEL_TYPE_LABELS[panel.panelType] || panel.panelType}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs — hidden for PP&E (it has its own section navigation) */}
        {!isPPE && (
          <div style={{ display: 'flex', gap: 2, marginTop: 14 }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  border: 'none', borderRadius: 6,
                  padding: '5px 14px', fontSize: 11, fontFamily: sans,
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  background: activeTab === tab.id ? green + '14' : 'transparent',
                  color: activeTab === tab.id ? green : t.textMuted,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {tab.label}
                {tab.id === 'tasks' && stats.total > 0 ? ` (${stats.total})` : ''}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: (activeTab === 'reconciliation' || activeTab === 'journal-entries') ? 0 : '12px 20px' }}>
        {activeTab === 'workpaper' && hasWorkpaper && (
          <PanelWorkpaperView panel={panel} glMappings={glMappings} selectedMonth={selectedMonth} />
        )}
        {activeTab === 'reconciliation' && isBankRec && (
          <BankRecPanel companyId={companyId} month={selectedMonth || '2026-02'} glMappings={glMappings} />
        )}
        {activeTab === 'journal-entries' && isBankRec && (
          <BankRecJournalEntries companyId={companyId} month={selectedMonth || '2026-02'} />
        )}
        {activeTab === 'tasks' && (
          <div>
            {/* Column headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '24px 1fr 70px 24px 24px 18px',
              alignItems: 'center',
              padding: '0 0 6px',
              gap: 8,
              borderBottom: `2px solid ${t.border}`,
              marginBottom: 4,
            }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase' }}>P</span>
              <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase' }}>Task</span>
              <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'right' }}>Due</span>
              <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'center' }}>A</span>
              <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'center' }}>R</span>
              <span />
            </div>

            {/* Tasks grouped by sub-section */}
            {subSections.map(sub => {
              const subTasks = tasks.filter(tk => tk.section === sub);
              const cleanLabel = sub.includes(' \u2014 ') ? sub.split(' \u2014 ').pop().trim() : sub;
              return (
                <div key={sub}>
                  {subSections.length > 1 && (
                    <div style={{
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.5, color: teal, fontFamily: sans,
                      padding: '10px 0 4px',
                      borderBottom: `2px solid ${teal}20`,
                      marginBottom: 2,
                    }}>
                      {cleanLabel}
                    </div>
                  )}
                  {subTasks.map(task => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      onToggleComplete={onToggleComplete}
                      onToggleReview={onToggleReview}
                      onToggleBlocked={onToggleBlocked}
                      onUpdateDeadline={onUpdateDeadline}
                      onOpenTask={onOpenTask}
                      onRemoveTask={onRemoveTask}
                      mockToday={mockToday}
                      userInitials={userInitials}
                      t={t}
                    />
                  ))}
                </div>
              );
            })}

            {tasks.length === 0 && !addingTask && (
              <div style={{
                textAlign: 'center', padding: '40px 20px',
                color: t.textFaint, fontSize: 12, fontStyle: 'italic',
              }}>
                No tasks for this panel yet.
              </div>
            )}

            {/* Add Task */}
            {onAddTask && (
              addingTask ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 0', borderTop: `1px solid ${t.border}`, marginTop: 8,
                }}>
                  <input
                    autoFocus
                    type="text"
                    placeholder="Task name..."
                    value={newTaskName}
                    onChange={e => setNewTaskName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newTaskName.trim()) {
                        onAddTask(newTaskName.trim());
                        setNewTaskName('');
                      }
                      if (e.key === 'Escape') {
                        setAddingTask(false);
                        setNewTaskName('');
                      }
                    }}
                    style={{
                      flex: 1, padding: '6px 10px', borderRadius: 6,
                      border: `1px solid ${green}60`, background: t.inputBg || '#F8FAFC',
                      fontSize: 12, fontFamily: sans, color: t.textPrimary, outline: 'none',
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newTaskName.trim()) {
                        onAddTask(newTaskName.trim());
                        setNewTaskName('');
                      }
                    }}
                    disabled={!newTaskName.trim()}
                    style={{
                      border: 'none', background: green, borderRadius: 6,
                      padding: '6px 14px', fontSize: 11, color: '#fff', cursor: 'pointer',
                      fontFamily: sans, fontWeight: 600, opacity: newTaskName.trim() ? 1 : 0.5,
                    }}
                  >Add</button>
                  <button
                    onClick={() => { setAddingTask(false); setNewTaskName(''); }}
                    style={{
                      border: `1px solid ${t.border}`, background: 'transparent', borderRadius: 6,
                      padding: '6px 10px', fontSize: 11, color: t.textMuted, cursor: 'pointer', fontFamily: sans,
                    }}
                  >Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingTask(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 0', marginTop: 8,
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    fontSize: 12, color: green, fontFamily: sans, fontWeight: 500,
                  }}
                >
                  + Add task
                </button>
              )
            )}
          </div>
        )}

        {activeTab === 'data' && (
          <div>
            {panelAccounts.length > 0 ? (
              <>
                {/* Total balance */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', borderRadius: 8, marginBottom: 12,
                  background: t.bgSurface || '#F8FAFC', border: `1px solid ${t.border}`,
                }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', marginBottom: 2 }}>
                      Total Balance ({panelAccounts.length} accounts)
                    </div>
                    <div style={{ fontSize: 9, color: t.textFaint }}>
                      From: {glMappings?.fileName || 'Imported TB'}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 20, fontWeight: 700, fontFamily: mono,
                    color: panelBalance < 0 ? red : t.textPrimary,
                  }}>
                    {panelBalance < 0 ? '$(' : '$'}
                    {Math.abs(panelBalance).toLocaleString('en-CA', { minimumFractionDigits: 0 })}
                    {panelBalance < 0 ? ')' : ''}
                  </div>
                </div>

                {/* Account list */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '70px 1fr 90px 90px 90px',
                  padding: '0 0 6px', gap: 8,
                  borderBottom: `2px solid ${t.border}`, marginBottom: 4,
                }}>
                  <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase' }}>Account</span>
                  <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase' }}>Name</span>
                  <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'right' }}>Debit</span>
                  <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'right' }}>Credit</span>
                  <span style={{ fontSize: 8, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'right' }}>Balance</span>
                </div>
                {panelAccounts.map((acc, i) => (
                  <div key={`${acc.code}-${i}`} style={{
                    display: 'grid', gridTemplateColumns: '70px 1fr 90px 90px 90px',
                    padding: '5px 0', gap: 8, alignItems: 'center',
                    borderBottom: `1px solid ${t.borderLight || t.border}`,
                  }}>
                    <span style={{ fontSize: 11, fontFamily: mono, fontWeight: 600, color: t.textPrimary }}>{acc.code}</span>
                    <span style={{ fontSize: 11, fontFamily: sans, color: t.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.name}</span>
                    <span style={{ fontSize: 11, fontFamily: mono, textAlign: 'right', color: acc.debit > 0 ? t.textPrimary : t.textFaint }}>
                      {acc.debit > 0 ? `$${acc.debit.toLocaleString('en-CA')}` : '--'}
                    </span>
                    <span style={{ fontSize: 11, fontFamily: mono, textAlign: 'right', color: acc.credit > 0 ? t.textPrimary : t.textFaint }}>
                      {acc.credit > 0 ? `$${acc.credit.toLocaleString('en-CA')}` : '--'}
                    </span>
                    <span style={{ fontSize: 11, fontFamily: mono, textAlign: 'right', fontWeight: 600, color: acc.balance < 0 ? red : t.textPrimary }}>
                      {acc.balance < 0 ? '$(' : '$'}{Math.abs(acc.balance).toLocaleString('en-CA')}{acc.balance < 0 ? ')' : ''}
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: t.textFaint, fontSize: 12 }}>
                <div style={{ fontSize: 14, marginBottom: 8, color: t.textMuted }}>
                  No GL accounts mapped
                </div>
                <div>Import a Trial Balance to see account data here.</div>
                <div style={{ marginTop: 8, fontSize: 10 }}>
                  Panel type: <span style={{ fontFamily: mono }}>{panel.panelType}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            {/* Collect all documents from tasks */}
            {(() => {
              const docs = tasks.flatMap(tk => (tk.documents || []).map(d => ({ doc: d, taskName: tk.name })));
              const workbooks = tasks.filter(tk => tk.workbook).map(tk => ({ workbook: tk.workbook, taskName: tk.name }));
              const uniqueWorkbooks = [...new Set(workbooks.map(w => w.workbook))];

              return (
                <>
                  {uniqueWorkbooks.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        Workbooks
                      </div>
                      {uniqueWorkbooks.map((wb, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                          border: `1px solid ${greenLight}`, background: greenLight + '20', borderRadius: 6, marginBottom: 4,
                        }}>
                          <span style={{ fontSize: 12 }}>{'\uD83D\uDCCA'}</span>
                          <span style={{ fontSize: 11, color: greenDark, fontWeight: 500, flex: 1 }}>{wb}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {docs.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        Attached Documents
                      </div>
                      {docs.map((d, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                          border: `1px solid ${t.border}`, borderRadius: 6, marginBottom: 3,
                        }}>
                          <span style={{ fontSize: 12 }}>{'\uD83D\uDCC4'}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: t.textPrimary }}>{d.doc}</div>
                            <div style={{ fontSize: 9, color: t.textFaint }}>{d.taskName}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{
                    marginTop: 12, padding: '16px', borderRadius: 8,
                    border: `2px dashed ${t.border}`,
                    textAlign: 'center', fontSize: 11, color: t.textFaint,
                  }}>
                    Drop files here or click to upload
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'activity' && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: t.textFaint, fontSize: 12 }}>
            Activity log will be available once connected to the backend.
          </div>
        )}
      </div>
    </div>
  );
}
